import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { FactoryProvider, Optional, PLATFORM_ID, makeStateKey, TransferState } from '@angular/core';

import { ApolloClientOptions, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { REQUEST } from '../../express.tokens';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink, Options } from 'apollo-angular/http';
import { Request } from 'express';

import { environment } from '../../environments/environment';
import possibleTypesResult from '../common/introspection-results';

const STATE_KEY = makeStateKey<any>('apollo.state');
let apolloCache: InMemoryCache;

export const APOLLO_CLIENT_PROVIDER: FactoryProvider = {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
    deps: [HttpLink, PLATFORM_ID, TransferState, [new Optional(), REQUEST]],
};

function mergeFields(existing: any, incoming: any) {
    return {...existing, ...incoming};
}

function relaceFields(existing: any, incoming: any) {
    return incoming;
}

// Trying to debug why sessions won't work in Safari 13.1
// but only on the live prod version.
function logInterceptorData(on: boolean) {
    localStorage.setItem('_logInterceptorData', on ? 'true' : 'false');
}

if (typeof window !== 'undefined') {
    (window as any).logInterceptorData = logInterceptorData;
}

export function apolloOptionsFactory(
    httpLink: HttpLink,
    platformId: object,
    transferState: TransferState,
    req?: Request,
): ApolloClientOptions<any> {
    const AUTH_TOKEN_KEY = 'auth_token';
    apolloCache = new InMemoryCache({
        possibleTypes: possibleTypesResult.possibleTypes,
        typePolicies: {
            Query: {
                fields: {
                    eligibleShippingMethods: {
                        merge: relaceFields,
                    },
                },
            },
            Product: {
                fields: {
                    customFields: {
                        merge: mergeFields,
                    },
                },
            },
            Collection: {
                fields: {
                    customFields: {
                        merge: mergeFields,
                    },
                },
            },
            Order: {
                fields: {
                    lines: {
                        merge: relaceFields,
                    },
                    shippingLines: {
                        merge: relaceFields,
                    },
                    discounts: {
                        merge: relaceFields,
                    },
                    shippingAddress: {
                        merge: relaceFields,
                    },
                    billingAddress: {
                        merge: relaceFields,
                    },
                },
            },
            Customer: {
                fields: {
                    addresses: {
                        merge: relaceFields,
                    },
                    customFields: {
                        merge: mergeFields,
                    },
                },
            },
        },
    });

    const {apiHost, apiPort, shopApiPath} = environment;
    const baseUri = `${apiHost}:${apiPort}/${shopApiPath}`;
    const ARABIC_REGEX = /[\u0600-\u06FF]/;
    const options: Options = {
        uri: (operation: any) => {
            if (isPlatformBrowser(platformId)) {
                let lang = localStorage.getItem('locale') || localStorage.getItem('app_language') || 'en';
                const term =
                    operation?.variables?.input?.term ||
                    operation?.variables?.term;
                if (term) {
                    lang = ARABIC_REGEX.test(String(term)) ? 'ar' : 'en';
                }
                if (lang && lang !== 'en') {
                    return `${baseUri}?languageCode=${lang}`;
                }
            }
            return baseUri;
        },
        withCredentials: false,
    };

    const http = httpLink.create(options);
    const afterware = new ApolloLink((operation, forward) => {
        return forward(operation).map((response) => {
            const context = operation.getContext();
            const authHeader = context.response.headers.get('vendure-auth-token');
            if (authHeader && isPlatformBrowser(platformId)) {
                localStorage.setItem(AUTH_TOKEN_KEY, authHeader);
            }
            return response;
        });
    });
    const middleware = new ApolloLink((operation, forward) => {
        if (isPlatformBrowser(platformId)) {
            operation.setContext({
                headers: new HttpHeaders().set(
                    'Authorization',
                    `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY) || null}`,
                ),
            });
        }
        return forward(operation);
    });

    const isBrowser = transferState.hasKey<any>(STATE_KEY);

    if (isBrowser) {
        // Skip SSR cache for non-English — data was fetched in English on server
        const lang = isPlatformBrowser(platformId)
            ? (localStorage.getItem('locale') || localStorage.getItem('app_language') || 'en')
            : 'en';
        if (lang === 'en') {
            const state = transferState.get<any>(STATE_KEY, null);
            apolloCache.restore(state);
        }
    } else {
        transferState.onSerialize(STATE_KEY, () => {
            return apolloCache.extract();
        });
        // Reset apolloCache after extraction to avoid sharing between requests
        apolloCache.reset();
    }

    return {
        cache: apolloCache,
        ssrMode: true,
        ssrForceFetchDelay: 500,
        link: ApolloLink.from([middleware, afterware, http]),
    };
}
